import { Outlet, useLocation, useParams } from 'react-router'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo } from 'react'
import {
  Avatar,
  Breadcrumbs,
  EmptyState,
  FullPageWrapper,
  SpinnerLoader,
  Tabs,
  Typography,
} from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { EnumUserRoles } from 'entities/Users'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { MembersPath } from 'pages/settings/pages/members/route/membersPath'
import { RoleChip } from 'pages/settings/pages/members/ui/RoleChip/RoleChip'
import { MembersRoutes } from 'pages/settings/pages/members/route/type'
import {
  useMemberContext,
  MemberProvider,
} from 'pages/settings/pages/members/context/memberContext'
import styles from './styles.module.scss'

const MemberLayoutContent = observer(() => {
  const {
    member,
    loading,
    memberPermissionsStore: { hasCustomPermissions, roleLabel },
  } = useMemberContext()
  const entities = useEntitiesContext()
  const { pathname } = useLocation()
  const { id } = useParams()

  const isOwner = useMemo(() => member?.role === EnumUserRoles.OWNER, [member])

  const tabs = useMemo(() => {
    const commonTabs = [
      { name: 'Profile', key: `/${MembersPath.root}/${id}/${MembersRoutes.profile}` },
      { name: 'Inboxes', key: `/${MembersPath.root}/${id}/${MembersRoutes.inboxes}` },
    ]

    if (isOwner) return commonTabs

    commonTabs.splice(1, 0, {
      name: 'Permissions',
      key: `/${MembersPath.root}/${id}/${MembersRoutes.permissions}`,
    })
    return commonTabs
  }, [isOwner, id])

  useEffect(() => {
    entities.permissionsStore.init()
  }, [])

  if (!member || loading || entities.permissionsStore.loading)
    return (
      <FullPageWrapper className={styles.wrapper}>
        <div className={styles.content}>
          <Breadcrumbs
            customPath={`/${MembersPath.root}`}
            getLabel={() => 'Members'}
            className={styles.breadcrumbs}
          />
          {loading || entities.permissionsStore.loading ? (
            <SpinnerLoader ariaLabel={'MemberLayoutContent'} />
          ) : (
            <EmptyState typographyProps={{ variant: 'body-sm-regular', ariaLabel: 'EmptyState' }} />
          )}
        </div>
      </FullPageWrapper>
    )

  return (
    <FullPageWrapper className={styles.wrapper}>
      <div className={styles.content}>
        <div className={styles.header}>
          <Breadcrumbs
            customPath={`/${MembersPath.root}`}
            getLabel={() => 'Members'}
            className={styles.breadcrumbs}
          />
          <div className={styles.profile}>
            <Avatar size={80} info={member.avatarInfo} />
            <div className={styles.profileInfo}>
              <Typography
                variant='heading-md'
                ariaLabel='MemberProfile_title'
                color='var(--content-primary-primary)'
                className={styles.name}
              >
                {member.name}
              </Typography>
              <Typography
                variant='body-sm-regular'
                ariaLabel='MemberProfile_email'
                color='var(--content-primary-tertiary)'
                className={styles.email}
              >
                {member.email}
              </Typography>
              <RoleChip
                roleLabel={roleLabel || member.role_label}
                hasCustomPermissions={hasCustomPermissions}
              />
            </div>
          </div>
          <Tabs
            tabsClassName={styles.tabs}
            tabs={tabs}
            activeTabKey={pathname}
            handleSelectTab={(tab) => {
              uiStore.changeRoute({
                path: tab.key,
              })
            }}
          />
        </div>
        <Outlet />
      </div>
    </FullPageWrapper>
  )
})

export const MemberLayout = observer(() => {
  return (
    <MemberProvider>
      <MemberLayoutContent />
    </MemberProvider>
  )
})
